/**
 * PATH: packages/pricelist/src/SectionTemplatePicker.tsx
 * FILE_ID: PKG-PRICELIST-SECTDLG-001
 * PURPOSE: Dialog to add a new section to a pricelist by picking an industry
 *          template (Steel, Waterproofing, …) or starting blank. The template
 *          only seeds the section's column_schema — every column stays editable.
 * WHY: Same universal engine for every trade; the template is data, not code.
 *      One pricelist can hold many sections from different industries.
 * FLOW: choose template + name → onSubmit({ name, columns }) → createSection
 * RELATED: industryTemplates.ts, usePricelistWorkbook (addSection), ColumnDialog
 */
import { useState } from 'react'
import type { ColumnDef } from './types'
import { INDUSTRY_TEMPLATES } from './industryTemplates'

export interface SectionTemplatePickerProps {
  open: boolean
  onClose: () => void
  onSubmit: (name: string, columns: ColumnDef[]) => void
}

const BLANK = '__blank__'

export default function SectionTemplatePicker({ open, onClose, onSubmit }: SectionTemplatePickerProps) {
  const [name, setName] = useState('')
  const [choice, setChoice] = useState<string>(BLANK)

  if (!open) return null

  const tpl = INDUSTRY_TEMPLATES.find((t) => t.key === choice)
  const effectiveName = name.trim() || tpl?.label || ''

  const submit = () => {
    if (!effectiveName) return
    onSubmit(effectiveName, tpl ? tpl.columns : [])
    setName('')
    setChoice(BLANK)
  }

  const optionClass = (active: boolean) =>
    'flex cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2 text-left ' +
    (active
      ? 'border-emerald-500 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950/30'
      : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-base font-semibold text-slate-900 dark:text-white">New section</h3>
        <p className="mb-3 text-[11px] text-slate-400">
          Pick an industry to pre-fill its columns — fully editable after. Add as many sections as
          your company has trades.
        </p>

        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
          Section name
        </label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={tpl?.label ?? 'e.g. Steelwork, Waterproofing, Site labour'}
          className="mb-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        />

        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
          Industry template
        </label>
        <div className="mb-3 grid max-h-[38vh] grid-cols-1 gap-1.5 overflow-y-auto pr-1">
          <button type="button" className={optionClass(choice === BLANK)} onClick={() => setChoice(BLANK)}>
            <span className="text-sm font-medium text-slate-900 dark:text-white">Blank</span>
            <span className="text-[11px] text-slate-400">
              Core columns only (code, description, cost, markup, sell). Add your own.
            </span>
          </button>
          {INDUSTRY_TEMPLATES.map((t) => (
            <button
              key={t.key}
              type="button"
              className={optionClass(choice === t.key)}
              onClick={() => setChoice(t.key)}
            >
              <span className="text-sm font-medium text-slate-900 dark:text-white">{t.label}</span>
              <span className="text-[11px] text-slate-400">{t.description}</span>
              <span className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                {t.columns.map((c) => c.label).join(' · ')}
              </span>
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!effectiveName}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            Create section
          </button>
        </div>
      </div>
    </div>
  )
}
